import { useEffect, useMemo, useState } from 'react';

import { Spinner, useColorMode, useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { first, isEmpty, isNull, last, shuffle, throttle } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import updateUserLocaleOrCountry from '../../components/api/mutations/updateUserLocaleOrCountry';
import updateUserPreferences from '../../components/api/mutations/updateUserPreferences';
import usePostsQuery from '../../components/api/queries/usePostsQuery';
import useRefreshJwtQuery from '../../components/api/queries/useRefreshJwtQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import AddUserProfileV1BottomSheet from '../../components/bottomSheet/AddUserProfileV1BottomSheet';
import PostsList from '../../components/community/PostsList';
import SelectFeedCountryBottomDrawer from '../../components/drawer/SelectFeedCountryBottomDrawer';
import ShowTradingProfileBottomDrawer from '../../components/drawer/ShowTradingProfileBottomDrawer';
import HomeHeader from '../../components/home/HomeHeader';
import CheckIcon from '../../components/icons/CheckIcon';
import i18n from '../../i18n/i18n';
import HomeBottomNav from '../../layout/HomeBottomNav';
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry
} from '../../utils/ExpiredLocalStorage';
import { openNewTab } from '../../utils/UIUtils';
import { getLanguage } from '../../utils/etcUtils';
import saveData from '../../utils/saveData';
import { Button } from '@peacecover/component';
import ToastActionWhite from '../../presentation/components/floating/ToastActionWhite';
import { Fab } from 'react-tiny-fab';
import LightColors from '../../constants/LightColors';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import FeatureGate from '../../domains/features/ui/components/FeatureGate';
import { FEATURE_KEYS } from '../../domains/features/constants';
import EnLocaleWrapper from '../../presentation/components/EnLocaleWrapper';

const PageWrapper = styled.div`
  padding-top: calc(env(safe-area-inset-top));
  background-attachment: scroll;
  height: 100vh;

  /* &.homeBackground {
    background: linear-gradient(
      180deg,
      #f8ebef 0%,
      #ffffff 6%,
      #ffffff 20%,
      #ffffff 32%,
      #cce4ef 100%
    );
  }

  &.darkHomeBackground {
    background: linear-gradient(
      180deg,
      #7e698b 0%,
      #222222 6%,
      #222222 20%,
      #222222 32%,
      #5c748f 100%
    );
  } */
`;

export default function FeedHomePage() {
  const { colorMode } = useColorMode();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const [selectedArtist, _setSelectedArtist] = useState();
  const [paginatedPosts, setPaginatedPosts] = useState();
  const [countryCode, setCountryCode] = useState(localStorage.getItem('feedCountryCode'));
  const [_currentPagination, setCurrentPagination] = useState();
  // const [isShowNewPostsNotification, setIsShowNewPostsNotification] = useState(false);
  const [isShowBaseProfileBottomSheet, setIsShowBaseProfileBottomSheet] = useState(false);
  const [isShowTradingProfileBottomSheet, setIsShowTradingProfileBottomSheet] = useState(false);
  const [isShowCountryDrawer, setIsShowCountryDrawer] = useState(false);
  // const [hiddenCollectedPhotoCardStat, setHiddenCollectedPhotoCardStat] = useState(localStorage.getItem("hiddenCollectedPhotoCardStat") === "true");

  const authToken = useRefreshJwtQuery();
  if (authToken?.jwt) {
    saveData('token', JSON.stringify(authToken?.jwt));
  }

  const isExpiredToShowTradingProfileBottomSheet = () => {
    const confirmedTradingProfileBottomSheet = getLocalStorageWithExpiry(
      'tradingProfileBottomSheetOnFeed'
    );
    return (
      confirmedTradingProfileBottomSheet === false || isNull(confirmedTradingProfileBottomSheet)
    );
  };

  const user = useUserQuery();

  useEffect(() => {
    if (isEmpty(user)) return;

    if (user && isEmpty(user?.baseUserProfile)) {
      return setIsShowBaseProfileBottomSheet(true);
    }

    if (!user?.showTradingProfile && isExpiredToShowTradingProfileBottomSheet()) {
      setIsShowTradingProfileBottomSheet(true);
    }

    if (isEmpty(user.locale) || getLanguage() === user.locale) return;
    saveData('i18nextLng', user.locale);
    i18n.changeLanguage(user.locale);
  }, [user]);

  if (user && isEmpty(user?.country) && !isEmpty(countryCode)) {
    updateUserLocaleOrCountry({ userId: user?.userId, country: countryCode });
    queryClient.invalidateQueries('users');
  }

  const shuffledFirstArtist = useMemo(() => {
    return first(shuffle(user?.likedArtists));
  }, [user]);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } = usePostsQuery({
    country: countryCode || user?.country,
    artistId: selectedArtist?.artistId,
    categoryGroup: 'market_place'
  });

  // const _artistId = getArtistIdAnyway(selectedArtist) || getArtistId();
  // const artist = useArtistQuery(artistId);

  useEffect(() => {
    if (!isEmpty(selectedArtist)) {
      setPaginatedPosts(undefined);
      setCurrentPagination(undefined);
      queryClient.resetQueries({
        queryKey: ['Posts', selectedArtist.artistId],
        exact: true
      });
      // Refetch only first pagination
      refetch({
        refetchPage: (_page, index) => index === 0,
        resetPageOnRefetch: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArtist]);

  const handlePaginatedPosts = (paginatedData) => {
    const posts = paginatedData.pages?.flatMap((page) =>
      page.posts.map((tradingPhotoCard) => tradingPhotoCard)
    );
    setPaginatedPosts(posts);
  };

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };
  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedPosts(data);
      setCurrentPagination(last(data.pages)?.meta?.pagination);
    }
  }, [data]);

  const navigateAddUserProfile = () => {
    navigate('/feed/create_feed_user_profile');
  };

  const navigateAddPost = () => {
    if (isEmpty(user?.baseUserProfile)) {
      navigateAddUserProfile();
    } else {
      let postAddUrl = '/posts/add';
      const queryParams = new URLSearchParams({
        category: 'Selling'
      });

      if (!isEmpty(selectedArtist)) {
        queryParams.append('artistId', selectedArtist.artistId);
        queryParams.append('fandomName', selectedArtist.fandomName);
      }
      postAddUrl = `${postAddUrl}/?${queryParams.toString()}`;
      openNewTab(postAddUrl);
    }
  };

  const showToastMessage = (postId) => {
    toast({
      position: 'top',
      duration: 2000,
      render: () => (
        <div className={'w-full flex justify-center mt-[100px]'}>
          <div
            className={
              'w-[338px] mx-auto flex items-center justify-between bg-black-opacity-500 dark:bg-white-opacity-600 h-[40px] rounded-[30px] pl-[16px] pr-[10px] py-[12px] shadow-[0_0_5px_0_rgba(0,0,0,0.08),0_0_12px_0_rgba(0,0,0,0.05)] dark:shadow-[0_0_5px_0_rgba(0,0,0,0.15),0_0_12px_0_rgba(0,0,0,0.20)]'
            }
          >
            <div className="flex gap-[4px] items-center">
              <div className="p-[2.5px]">
                <CheckIcon className="fill-white bg-onSuccess-light dark:bg-onSuccess-dark rounded-full w-[15px] h-[15px] p-[3px]" />
              </div>
              <p className="text-pure-white dark:text-pure-black text-[14px] font-medium leading-none">
                {t('feedPage.uploadToastMessage')}
              </p>
            </div>
            <Button
              size={'small'}
              onClick={() => openNewTab(`/posts/${postId}`)}
              className="px-[12px] py-[6px] flex items-center text-[13px]"
            >
              {t('feedPage.uploadToastShowPost')}
            </Button>
          </div>
        </div>
      )
    });
  };

  useEffect(() => {
    const receiveMessage = throttle((event) => {
      if (event.origin !== window.location.origin) return;
      if (isEmpty(event.data?.type)) return;

      if (event.data?.type === 'post-creation-complete') {
        const post = event.data?.params?.post;
        if (!isEmpty(post)) {
          showToastMessage(post.postId);
          queryClient.invalidateQueries('Posts');
        }
      } else if (event.data?.type === 'invalidated-user-profile') {
        queryClient.invalidateQueries('users');
        queryClient.invalidateQueries(['userProfile']);
      }
    }, 1000);

    window.addEventListener('message', receiveMessage, false);

    return () => {
      window.removeEventListener('message', receiveMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  // #region pull to refresh
  const [refreshing, setRefreshing] = useState(false);
  const [lastY, setLastY] = useState(null);
  const spinnerAnimatedProps = useSpring({
    to: {
      transform: refreshing ? 'translateY(88px)' : 'translateY(0px)',
      opacity: refreshing ? 1 : 0
    },
    from: {
      transform: 'translateY(0px)',
      opacity: 0
    }
  });

  const bodyAnimatedProps = useSpring({
    to: {
      transform: refreshing ? 'translateY(88px)' : 'translateY(0px)'
    },
    from: {
      transform: 'translateY(0px)'
    }
  });

  const handleTouchStart = (e) => {
    setLastY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    const newY = e.touches[0].clientY;
    const deltaY = newY - lastY;
    if (refreshing) {
      return;
    }
    if (deltaY > 150 && window.scrollY <= 0) {
      setRefreshing(true);
      const startTime = Date.now(); // 현재 시간 기록
      refetch({
        refetchPage: (_page, index) => index === 0,
        resetPageOnRefetch: true
      }).then(() => {
        const elapsedTime = Date.now() - startTime; // 경과 시간 계산
        const remainingTime = Math.max(1500 - elapsedTime, 0); // 남은 시간 계산
        setTimeout(() => {
          setRefreshing(false);
        }, remainingTime);
      });
    }
  };
  // #endregion

  // const onHiddenCollectedPhotoCardStat = () => {
  //   saveData("hiddenCollectedPhotoCardStat", true);
  //   setHiddenCollectedPhotoCardStat(true);
  // }

  const onConfirmedCountryCode = (countryCode) => {
    saveData('feedCountryCode', countryCode);
    queryClient.invalidateQueries('Posts');
    setCountryCode(countryCode);
    setIsShowCountryDrawer(false);
  };

  const onCloseShowTradingProfileBottomSheet = () => {
    const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;
    setLocalStorageWithExpiry('tradingProfileBottomSheetOnFeed', true, ONE_WEEK);
    setIsShowTradingProfileBottomSheet(false);
  };

  const onUpdateUserPreferences = async () => {
    const response = await updateUserPreferences({ showTradingProfile: true });
    if (response?.ok) {
      setIsShowTradingProfileBottomSheet(false);
      queryClient.invalidateQueries('users');
      queryClient.invalidateQueries(['userProfile']);
    }
  };

  const onClickProfile = () => {
    if (isEmpty(user?.baseUserProfile)) {
      setIsShowBaseProfileBottomSheet(true);
    } else {
      openNewTab(`/feed/user_profile/${user?.baseUserProfile?.userProfileId}`);
    }
  };

  const isShowingBottomSheet = () => {
    return (
      isShowBaseProfileBottomSheet ||
      isShowCountryDrawer ||
      isShowTradingProfileBottomSheet ||
      isShowBaseProfileBottomSheet
    );
  };

  return (
    <PageWrapper onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
      <Helmet>
        <title>{t('tabs.feed')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <animated.div
        style={{
          ...spinnerAnimatedProps,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: refreshing ? 'auto' : '0px'
        }}
      >
        <Spinner color="#C2C1C7" />
      </animated.div>

      <animated.div style={bodyAnimatedProps}>
        <HomeHeader
          title={t('feedPage.title')}
          user={user}
          onClickTitle={() => setIsShowCountryDrawer(true)}
          onClickProfileButton={onClickProfile}
        />
        <FeatureGate featureKey={FEATURE_KEYS.CARD_REVIEW} inverted>
          <EnLocaleWrapper>
            <div className="px-[11px] mt-[10px] w-full">
              <ToastActionWhite
                emoji="💡"
                text={t('feedPage.recommendTradingTitle')}
                rightText={t('feedPage.goToTrading')}
                onClick={() => navigate('/tradings/photo_cards')}
                hasBorder={true}
              />
            </div>
          </EnLocaleWrapper>
        </FeatureGate>
        <PostsList
          user={user}
          posts={paginatedPosts}
          artistId={selectedArtist?.artistId || shuffledFirstArtist?.artistId}
          loadMore={loadMore}
          hasMore={hasNextPage}
        />
      </animated.div>
      <HomeBottomNav tabIndex={2} />
      {!isShowingBottomSheet() && (
        <div
          onClick={(e) => {
            // FAB 버튼 내부에서 클릭된 경우에만 실행
            if (e.target.closest('[role="button"]')) {
              navigateAddPost();
            }
          }}
          role="none"
        >
          <Fab
            alwaysShowTitle={false}
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 64px)', right: 0 }}
            icon={
              <PlusIcon
                className="fill-pure-white transition-none"
                width={'24px'}
                height={'24px'}
              />
            }
            mainButtonStyles={{
              backgroundColor: LightColors.primary,
              boxShadow:
                '0px 0px 4px 0px rgba(92, 61, 250, 0.40), 0px 2px 10px 0px rgba(92, 61, 250, 0.30)'
            }}
            event="hover"
          />
        </div>
      )}

      {isShowCountryDrawer && (
        <SelectFeedCountryBottomDrawer
          isOpen={isShowCountryDrawer}
          onClose={() => setIsShowCountryDrawer(false)}
          defaultCountryCode={user?.country}
          onConfirmedCountryCode={onConfirmedCountryCode}
        />
      )}
      {isShowTradingProfileBottomSheet && (
        <ShowTradingProfileBottomDrawer
          isOpen={isShowTradingProfileBottomSheet}
          onClose={() => onCloseShowTradingProfileBottomSheet()}
          onUpdateUserPreferences={onUpdateUserPreferences}
        />
      )}
      {isShowBaseProfileBottomSheet && (
        <AddUserProfileV1BottomSheet
          title={t('createFeedUserProfilePage.title')}
          isShowProfileBottomSheet={isShowBaseProfileBottomSheet}
          setIsShowProfileBottomSheet={setIsShowBaseProfileBottomSheet}
          category={'base'}
        />
      )}
    </PageWrapper>
  );
}
